import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import settings2Fill from '@iconify/icons-eva/settings-2-fill';
import calendarFill from '@iconify/icons-eva/calendar-fill';

import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { alpha } from '@mui/material/styles';
import { Badge, Button, Box, Divider, MenuItem, Typography } from '@mui/material';

import { PATH_APP } from 'routes/paths';
import endPoints from 'endPoints/endPoints';

import useAuth from 'hooks/useAuth';
import useIsMountedRef from 'hooks/useIsMountedRef';
import ApiServiceFecth from 'service/ApiServiceFecth';

import { MIconButton } from 'components/@material-extend';
import MyAvatar from 'components/MyAvatar';
import MenuPopover from 'components/MenuPopover';

const MENU_OPTIONS = [
  {
    label: 'Dashboard',
    icon: settings2Fill,
    linkTo: PATH_APP.dashboard
  },
  {
    label: 'Mi semana',
    icon: calendarFill,
    linkTo: PATH_APP.kanbanMyWeek
  }
];

const toISODate = (value) => {
  const d = new Date(value);
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const startOfWeekIso = (value = new Date()) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const delta = day === 0 ? -6 : (1 - day);
  d.setDate(d.getDate() + delta);
  return toISODate(d);
};

export default function AccountPopover() {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pendingWeekCount, setPendingWeekCount] = useState(0);

  const loadPendingWeekCount = useCallback(async () => {
    const response = await ApiServiceFecth.get({
      url: endPoints.admin.kanban.myWeek,
      data: { week_start: startOfWeekIso() },
    });
    if (!response?.ok) return;
    const json = await response.json();
    const days = Array.isArray(json?.data?.days) ? json.data.days : [];
    const pending = days.reduce((acc, day) => {
      const items = Array.isArray(day?.items) ? day.items : [];
      return acc + items.filter((item) => !item?.is_done).length;
    }, 0);
    if (isMountedRef.current) {
      setPendingWeekCount(pending);
    }
  }, [isMountedRef]);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    loadPendingWeekCount();
  }, [loadPendingWeekCount]);

  useEffect(() => {
    if (!open) return;
    loadPendingWeekCount();
  }, [open, loadPendingWeekCount]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout', { variant: 'error' });
    }
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        <Badge
          color="error"
          overlap="circular"
          badgeContent={pendingWeekCount > 99 ? '99+' : pendingWeekCount}
          invisible={pendingWeekCount <= 0}
        >
          <MyAvatar />
        </Badge>
      </MIconButton>

      <MenuPopover open={open} onClose={handleClose} anchorEl={anchorRef.current} sx={{ width: 220 }}>
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {user?.name || 'Admin'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email || '-'}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {MENU_OPTIONS.map((option) => (
          <MenuItem
            key={option.label}
            to={option.linkTo}
            component={RouterLink}
            onClick={handleClose}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Box
              component={Icon}
              icon={option.icon}
              sx={{
                mr: 2,
                width: 24,
                height: 24
              }}
            />
            {option.label}
          </MenuItem>
        ))}

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button fullWidth color="inherit" variant="outlined" onClick={handleLogout}>
            Logout
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
